import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { debounce, get } from 'lodash';
import { mapStateToProps } from '../components/QueryFilterContainer';
import { updateQueryFilterValues } from '../actions';
import { fetchClauseCounts } from '../../profile/actions';
import { refreshCohortVisualisation } from '../../visualisations/actions';
import CohortFilterLookupsDropdown from './CohortFilterLookupsDropdown';
import {
  cohortArrayOperators,
  cohortEqualsLikeOperators,
  cohortEqualsOperators,
  cohortGreatLessEqualsOperators,
  cohortJoinOperators,
  cohortRangeOperators,
} from '../constants';
import CohortFilterLookupsMultiple from '../components/CohortFilterLookupsMultiple';
import CohortFilterJoinsDropdown from './CohortFilterJoinsDropdown';
import CohortFilterValuesDate from '../components/CohortFilterValuesDate';
import CohortFilterValuesDateRange from '../components/CohortFilterValuesDateRange';
import CohortFilterValuesNumber from '../components/CohortFilterValuesNumber';
import CohortFilterValuesNumberRange from '../components/CohortFilterValuesNumberRange';
import CohortFilterValuesNumbers from '../components/CohortFilterValuesNumbers';
import CohortFilterValuesInput from '../components/CohortFilterValuesInput';
import CohortFilterValuesDropdown from '../components/CohortFilterValuesDropdown';
import CohortFilterValuesToggle from '../components/CohortFilterValuesToggle';

const cohortFilter = (component, operators, hasLookups = false) => ({
  component,
  operators: Object.values(operators),
  hasLookups,
});

const cohortFilterLookup = () => [
  cohortFilter(CohortFilterLookupsDropdown, cohortEqualsOperators, true),
  cohortFilter(CohortFilterLookupsMultiple, cohortArrayOperators, true),
];

const cohortFilterLookupWithJoins = () => [
  ...cohortFilterLookup(),
  cohortFilter(CohortFilterJoinsDropdown, cohortJoinOperators, true),
];

const dateComponents = () => [
  cohortFilter(CohortFilterValuesDate, cohortGreatLessEqualsOperators),
  cohortFilter(CohortFilterValuesDateRange, cohortRangeOperators),
];

const numericComponents = () => [
  cohortFilter(CohortFilterValuesNumber, cohortGreatLessEqualsOperators),
  cohortFilter(CohortFilterValuesNumberRange, cohortRangeOperators),
  cohortFilter(CohortFilterValuesNumbers, cohortArrayOperators),
  cohortFilter(CohortFilterJoinsDropdown, cohortJoinOperators),
];

const textComponents = () => [
  cohortFilter(CohortFilterValuesInput, cohortEqualsLikeOperators),
  cohortFilter(CohortFilterValuesDropdown, cohortArrayOperators),
  cohortFilter(CohortFilterJoinsDropdown, cohortJoinOperators),
];

const cohortFilterComponents = {
  boolean: [cohortFilter(CohortFilterValuesToggle, { other: [cohortEqualsOperators.EQUALS] })],
  date: [...dateComponents(), ...cohortFilterLookup()],
  time: [...dateComponents(), ...cohortFilterLookup()],
  datetime: [...dateComponents(), ...cohortFilterLookup()],
  decimal: [...numericComponents(), ...cohortFilterLookupWithJoins()],
  integer: [...numericComponents(), ...cohortFilterLookupWithJoins()],
  text: [...textComponents(), ...cohortFilterLookupWithJoins()],
};

const QueryFilterValuesEditor = ({
  category,
  code,
  fieldConstraints,
  values,
  type,
  operator,
  queryUUID,
  clauseUUID,
  filterUUID,
  dictionary,
  updateValues,
  fetchCounts,
  fetchVisualisation,
  saveErrors,
}) => {
  const hasLookups = get(dictionary.lookups, `${fieldConstraints}.options`, []).length > 0;
  const components = cohortFilterComponents[type];
  const component = components.find((c) => c.operators.includes(operator) && c.hasLookups === hasLookups)
      || components[0];

  const refreshCounts = useMemo(() => debounce(fetchCounts, 500), []);
  const refreshVisualisation = useMemo(() => debounce(fetchVisualisation, 700), []);

  const updateFilterValues = (newValues) => {
    updateValues(category, code, queryUUID, clauseUUID, filterUUID, newValues);
    refreshCounts(category, code, queryUUID, clauseUUID, true);
    refreshVisualisation(category, code, queryUUID);
  };

  const FieldComponent = component.component;
  return (
    <FieldComponent
      category={category}
      code={code}
      values={values}
      onChange={updateFilterValues}
      fieldType={type}
      operator={operator}
      queryUUID={queryUUID}
      clauseUUID={clauseUUID}
      filterUUID={filterUUID}
      dictionaryCode={dictionary.code}
      saveErrors={saveErrors}
    />
  );
};

export const mapDispatchToProps = {
  updateValues: updateQueryFilterValues,
  fetchCounts: fetchClauseCounts,
  fetchVisualisation: refreshCohortVisualisation,
};

export default connect(mapStateToProps, mapDispatchToProps)(QueryFilterValuesEditor);
